import React, {useCallback, useEffect, useRef, useState} from "react";
import PositionCell from "./PositionCell";
import { DisplayFields, PositionData } from "./types";
import { RowKey, UIPair } from "../../helpers/useHoldingsHistoryHelper";
import "../../styles/scrollbar.css";
import { AumPill, TickerPill, AumTier } from "./InstitutionSelector";

const typeLabels: Record<string, string> = {
    SHARE: "Share",
    CALL: "Call / Long",
    PUT: "Put / Short",
};

const prettyTicker = (t?: string | null): string => {
    if (!t) return "";
    const base = t.includes("_") ? t.split("_")[0] : t;
    if (!base) return "";
    return base.toLowerCase() === "null" ? "" : base;
};

const yqToNum = (s: string): number => {
    const m = /(\d{4})\s*[- ]?\s*Q?([1-4])/i.exec(s);
    return m ? Number(m[1]) * 10 + Number(m[2]) : NaN;
};
const yqGte = (a: string, b: string) => yqToNum(a) >= yqToNum(b);
const NEW_FLOOR_YQ = "2013Q3";

type ColumnMetaByPeriod = Record<
    string,
    {
        period: string;
        snapshotId: number;
        baseReportId: number | null;
        report?: {
            id: number;
            filingUri: string;
            isReportedTableValueTotalSuspect: boolean;
            isProcessedTableValueTotalSuspect: boolean;
            processedInfoTableValueUnitEnum?: string;
            reportedInfoTableValueUnitEnum?: string;
        };
    }
>;

type InstitutionMeta = { name: string; ticker: string | null; tier: AumTier };

const SuspectBadge: React.FC<{ kind: "R" | "P"; title: string }> = ({ kind, title }) => (
    <span
        title={title}
        style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 16,
            height: 16,
            borderRadius: 999,
            fontSize: 10,
            fontWeight: 800,
            marginLeft: 6,
            background: kind === "R" ? "#f87171" : "#fbbf24",
            color: "#1b0f0f",
            border: "1px solid rgba(0,0,0,0.25)",
        }}
    >
    {kind}
  </span>
);

const UnitChip: React.FC<{ unit?: string }> = ({ unit }) => {
    if (!unit) return null;
    return (
        <span
            title={`Value unit: ${unit}`}
            style={{
                marginLeft: 8,
                padding: "1px 6px",
                borderRadius: 6,
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.12)",
                fontSize: 10.5,
                fontWeight: 700,
            }}
        >
      {unit}
    </span>
    );
};

export default function TableView({
                                      allYearQuarters,
                                      pairs,
                                      rowPivot,
                                      totalsByYQ,
                                      totalValueInNewestQuarter,
                                      showPositionShare,
                                      displayFields,
                                      institutionMap,
                                      institutionMetaMap,
                                      mode,
                                      aggregatePositions,
                                      columnMetaByPeriod,
                                  }: {
    allYearQuarters: string[];
    pairs: UIPair[];
    rowPivot: Record<RowKey, Record<string, PositionData>>;
    totalsByYQ?: Record<string, number>;
    totalValueInNewestQuarter?: number;
    showPositionShare: boolean;
    displayFields: DisplayFields;
    institutionMap: Record<number, string>;
    institutionMetaMap: Record<number, InstitutionMeta>;
    mode: "institution" | "ticker";
    aggregatePositions: boolean;
    columnMetaByPeriod: ColumnMetaByPeriod;
}) {
    const leftWidth = 270;
    const TYPE_COL_W = 100;
    const QUARTER_COL_W = 300;

    const STICKY_W = leftWidth + TYPE_COL_W;
    const rightWidth = QUARTER_COL_W * Math.max(0, allYearQuarters.length);
    const contentWidth = STICKY_W + rightWidth;

    const mainRef = useRef<HTMLDivElement>(null);
    const topRef = useRef<HTMLDivElement>(null);

    // header right now uses a real scroll container (hidden scrollbar)
    const headRightViewportRef = useRef<HTMLDivElement>(null);

    const leftFadeRef = useRef<HTMLDivElement>(null);
    const rightFadeRef = useRef<HTMLDivElement>(null);

    const rAF = useRef<number | null>(null);
    const syncing = useRef(false);

    const [needsScroll, setNeedsScroll] = useState(false);
    const [maxScroll, setMaxScroll] = useState(0);

    const TOP_SCROLLBAR_H = 12;
    const TOP_SCROLLBAR_MARGIN_TOP = 6;
    const TOP_SCROLLBAR_MARGIN_BOTTOM = 2;
    const HEADER_H = 44;
    const STICKY_STACK_H =
        (needsScroll ? TOP_SCROLLBAR_H + TOP_SCROLLBAR_MARGIN_TOP + TOP_SCROLLBAR_MARGIN_BOTTOM : 0) + HEADER_H;

    const gpuText: React.CSSProperties = {
        willChange: "transform",
        transform: "translateZ(0)",
        backfaceVisibility: "hidden",
        contain: "paint",
    };

    const updateFades = useCallback((scrollLeft: number) => {
        if (!leftFadeRef.current || !rightFadeRef.current) return;
        leftFadeRef.current.style.opacity = scrollLeft > 0 ? "1" : "0";
        rightFadeRef.current.style.opacity = scrollLeft < maxScroll - 1 ? "1" : "0";
    }, [maxScroll]);

    useEffect(() => {
        const el = mainRef.current;
        if (!el) return;

        const measure = () => {
            const visibleRight = el.clientWidth - STICKY_W;
            setNeedsScroll(rightWidth > visibleRight + 1);
            const newMax = Math.max(0, el.scrollWidth - el.clientWidth);
            setMaxScroll(newMax);

            const x = el.scrollLeft;
            if (topRef.current) topRef.current.scrollLeft = x;
            if (headRightViewportRef.current) headRightViewportRef.current.scrollLeft = x;
            updateFades(x);
        };

        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, [rightWidth, STICKY_W, updateFades]);

    const onMainScroll: React.UIEventHandler<HTMLDivElement> = (e) => {
        if (syncing.current) return;
        const el = e.currentTarget;
        if (rAF.current != null) cancelAnimationFrame(rAF.current);
        rAF.current = requestAnimationFrame(() => {
            const x = el.scrollLeft;
            // mirror native scroll to header scroller + top scrollbar
            if (topRef.current) topRef.current.scrollLeft = x;
            if (headRightViewportRef.current) headRightViewportRef.current.scrollLeft = x;
            updateFades(x);
        });
    };

    const onTopScroll: React.UIEventHandler<HTMLDivElement> = () => {
        if (syncing.current) return;
        const top = topRef.current;
        const main = mainRef.current;
        const head = headRightViewportRef.current;
        if (!top || !main || !head) return;

        if (rAF.current != null) cancelAnimationFrame(rAF.current);
        rAF.current = requestAnimationFrame(() => {
            syncing.current = true;
            const x = top.scrollLeft;
            main.scrollLeft = x;          // native body scroll
            head.scrollLeft = x;          // native header scroll
            updateFades(x);
            syncing.current = false;
        });
    };

    const blockHorizontalWheel: React.WheelEventHandler = (e) => {
        if (e.ctrlKey) return;
        if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
            e.stopPropagation();
            e.preventDefault();
        }
    };

    useEffect(() => {
        const el = mainRef.current;
        return () => {
            if (el) el.style.pointerEvents = "auto";
        };
    }, []);
    const onLeftPointerDown: React.PointerEventHandler = (e) => {
        if (e.pointerType === "touch" && e.isPrimary) {
            if (mainRef.current) mainRef.current.style.pointerEvents = "none";
        }
    };
    const onLeftPointerUpOrCancel: React.PointerEventHandler = () => {
        if (mainRef.current) mainRef.current.style.pointerEvents = "auto";
    };

    return (
        <div style={{ position: "relative", width: "100%" }}>
            <div style={{ position: "sticky", top: 0, zIndex: 7, background: "#141414" }}>
                {needsScroll && (
                    <div
                        ref={topRef}
                        className="scrollbarDark"
                        onScroll={onTopScroll}
                        style={{
                            width: `calc(100% - ${STICKY_W}px)`,
                            marginLeft: STICKY_W,
                            overflowX: "auto",
                            overflowY: "hidden",
                            height: TOP_SCROLLBAR_H,
                            marginTop: TOP_SCROLLBAR_MARGIN_TOP,
                            marginBottom: TOP_SCROLLBAR_MARGIN_BOTTOM,
                            scrollbarWidth: "thin",
                            WebkitOverflowScrolling: "touch",
                            scrollbarGutter: "stable",
                            borderRadius: 8,
                            touchAction: "pan-x pan-y pinch-zoom",
                        }}
                        aria-hidden
                    >
                        <div style={{ width: rightWidth, height: 1 }} />
                    </div>
                )}

                <div
                    style={{
                        display: "flex",
                        alignItems: "stretch",
                        borderBottom: "1px solid rgba(255,255,255,0.08)",
                        background: "#232323",
                    }}
                >
                    <div
                        onPointerDown={onLeftPointerDown}
                        onPointerUp={onLeftPointerUpOrCancel}
                        onPointerCancel={onLeftPointerUpOrCancel}
                        onWheelCapture={blockHorizontalWheel}
                        style={{
                            width: leftWidth,
                            minWidth: leftWidth,
                            padding: "12px 12px",
                            fontWeight: 900,
                            fontSize: 13,
                            boxShadow: "inset -1px 0 0 rgba(255,255,255,0.06)",
                            touchAction: "pan-x pan-y pinch-zoom",
                            overscrollBehaviorX: "contain",
                        }}
                    >
                        {mode === "ticker" ? "Institution" : "Holding"}
                    </div>

                    <div
                        onPointerDown={onLeftPointerDown}
                        onPointerUp={onLeftPointerUpOrCancel}
                        onPointerCancel={onLeftPointerUpOrCancel}
                        onWheelCapture={blockHorizontalWheel}
                        style={{
                            width: TYPE_COL_W,
                            minWidth: TYPE_COL_W,
                            padding: "12px 10px",
                            fontWeight: 900,
                            fontSize: 13,
                            boxShadow: "inset -1px 0 0 rgba(255,255,255,0.06)",
                            touchAction: "pan-x pan-y pinch-zoom",
                            overscrollBehaviorX: "contain",
                        }}
                    >
                        Type
                    </div>

                    {/* Header right: real scroll container (hidden) that mirrors mainRef.scrollLeft */}
                    <div
                        ref={headRightViewportRef}
                        style={{
                            width: `calc(100% - ${STICKY_W}px)`,
                            marginLeft: "auto",
                            overflowX: "hidden", // hidden scrollbar, but still scrollLeft-syncable
                            overflowY: "hidden",
                            position: "relative",
                        }}
                    >
                        <div
                            style={{
                                width: rightWidth,
                                display: "grid",
                                gridAutoFlow: "column",
                                gridAutoColumns: `${QUARTER_COL_W}px`,
                                boxSizing: "border-box",
                            }}
                        >
                            {allYearQuarters.map((yq) => {
                                const meta = columnMetaByPeriod[yq];
                                const hasR = !!meta?.report?.isReportedTableValueTotalSuspect;
                                const hasP = !!meta?.report?.isProcessedTableValueTotalSuspect;
                                const unit =
                                    meta?.report?.processedInfoTableValueUnitEnum ||
                                    meta?.report?.reportedInfoTableValueUnitEnum;

                                return (
                                    <div
                                        key={yq}
                                        style={{
                                            padding: "12px 10px",
                                            fontWeight: 900,
                                            fontSize: 13,
                                            borderLeft: "1px solid rgba(255,255,255,0.06)",
                                            contain: "paint",
                                            boxSizing: "border-box",
                                        }}
                                    >
                                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                            <span>{yq}</span>
                                            {unit && <UnitChip unit={unit} />}
                                            {hasR && <SuspectBadge kind="R" title="Header total marked suspect" />}
                                            {hasP && <SuspectBadge kind="P" title="Processed total marked suspect" />}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>

            {/* Fades */}
            <div
                ref={leftFadeRef}
                style={{
                    position: "absolute",
                    left: 0,
                    top: STICKY_STACK_H,
                    bottom: 0,
                    width: 24,
                    pointerEvents: "none",
                    background:
                        "linear-gradient(90deg, rgba(20,20,20,1) 0%, rgba(20,20,20,0.75) 40%, rgba(20,20,20,0) 100%)",
                    zIndex: 3,
                    opacity: 0,
                    transition: "opacity 120ms linear",
                }}
            />
            <div
                ref={rightFadeRef}
                style={{
                    position: "absolute",
                    right: 0,
                    top: STICKY_STACK_H,
                    bottom: 0,
                    width: 14,
                    pointerEvents: "none",
                    background:
                        "linear-gradient(270deg, rgba(20,20,20,0.45) 0%, rgba(20,20,20,0.25) 40%, rgba(20,20,20,0) 100%)",
                    zIndex: 3,
                    opacity: 0,
                    transition: "opacity 120ms linear",
                }}
            />

            {/* Main scroller */}
            <div
                ref={mainRef}
                className="scrollbarDark"
                onScroll={onMainScroll}
                style={{
                    position: "relative",
                    width: "100%",
                    overflowX: "auto",
                    overflowY: "visible",
                    paddingBottom: 8,
                    scrollbarWidth: "thin",
                    WebkitOverflowScrolling: "touch",
                    scrollbarGutter: "stable",
                    touchAction: "pan-x pan-y pinch-zoom",
                }}
            >
                <table
                    style={{
                        width: `${contentWidth}px`,
                        tableLayout: "fixed",
                        borderCollapse: "separate",
                        borderSpacing: 0,
                        backgroundColor: "#141414",
                        color: "#fff",
                    }}
                >
                    <tbody>
                    {pairs.map((row) => {
                        const cells = rowPivot[row.rowKey] ?? {};
                        const firstPos = cells[allYearQuarters[0]] || Object.values(cells)[0];

                        const displayTicker = prettyTicker(row.ticker);
                        const title = firstPos?.title || "";
                        const cusip = (firstPos as any)?.cusip;

                        const leftTop =
                            mode === "ticker"
                                ? aggregatePositions
                                    ? "All Institutions"
                                    : row.institutionId
                                        ? institutionMap[row.institutionId] ?? `Institution ${row.institutionId}`
                                        : "Institution"
                                : displayTicker || "—";

                        const meta: InstitutionMeta | undefined =
                            mode === "ticker" && !aggregatePositions && row.institutionId
                                ? institutionMetaMap[row.institutionId]
                                : undefined;

                        const showSubtitle = mode !== "ticker";
                        const pillsAboveName = mode === "ticker" && !aggregatePositions && !!meta;

                        return (
                            <tr key={row.rowKey} style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                                <td
                                    onPointerDown={onLeftPointerDown}
                                    onPointerUp={onLeftPointerUpOrCancel}
                                    onPointerCancel={onLeftPointerUpOrCancel}
                                    onWheelCapture={blockHorizontalWheel}
                                    style={{
                                        position: "sticky",
                                        left: 0,
                                        zIndex: 3,
                                        backgroundColor: "#1c1c1c",
                                        width: leftWidth,
                                        minWidth: leftWidth,
                                        padding: "10px 12px",
                                        lineHeight: 1.2,
                                        wordBreak: "break-word",
                                        whiteSpace: "normal",
                                        borderRight: "1px solid rgba(255,255,255,0.06)",
                                        willChange: "transform",
                                        transform: "translateZ(0)",
                                        verticalAlign: "middle",
                                        touchAction: "pan-x pan-y pinch-zoom",
                                        overscrollBehaviorX: "contain",
                                    }}
                                >
                                    {pillsAboveName ? (
                                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                                                <AumPill tier={meta!.tier} />
                                                {meta?.ticker ? <TickerPill ticker={meta.ticker} /> : null}
                                            </div>
                                            <div
                                                style={{
                                                    fontSize: 14,
                                                    fontWeight: 900,
                                                    minWidth: 0,
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                                title={leftTop}
                                            >
                                                {leftTop}
                                            </div>
                                        </div>
                                    ) : (
                                        <div
                                            style={{
                                                display: "grid",
                                                gridTemplateColumns: meta ? "minmax(0,1fr) max-content" : "1fr",
                                                alignItems: "center",
                                                columnGap: 8,
                                                marginBottom: showSubtitle ? 4 : 0,
                                            }}
                                        >
                                            <div
                                                style={{
                                                    fontSize: 14,
                                                    fontWeight: 900,
                                                    minWidth: 0,
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                                title={leftTop}
                                            >
                                                {leftTop}
                                            </div>
                                            {meta && (
                                                <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                                    {meta.ticker ? <TickerPill ticker={meta.ticker} /> : null}
                                                    <AumPill tier={meta.tier} />
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {showSubtitle && title && <div style={{ fontSize: 12.5, color: "#e8e8e8" }}>{title}</div>}
                                    {showSubtitle && cusip && (
                                        <div style={{ fontSize: 11.5, color: "#bdbdbd", marginTop: 2 }}>{cusip}</div>
                                    )}
                                </td>

                                <td
                                    onPointerDown={onLeftPointerDown}
                                    onPointerUp={onLeftPointerUpOrCancel}
                                    onPointerCancel={onLeftPointerUpOrCancel}
                                    onWheelCapture={blockHorizontalWheel}
                                    style={{
                                        position: "sticky",
                                        left: leftWidth,
                                        zIndex: 2,
                                        width: TYPE_COL_W,
                                        minWidth: TYPE_COL_W,
                                        fontSize: 12.5,
                                        color:
                                            row.type === "SHARE"
                                                ? "#fff"
                                                : row.type === "CALL"
                                                    ? "#34d399"
                                                    : "#f87171",
                                        fontWeight: row.type === "SHARE" ? ("bold" as const) : ("normal" as const),
                                        padding: "10px",
                                        backgroundColor: "#1c1c1c",
                                        borderRight: "1px solid rgba(255,255,255,0.06)",
                                        willChange: "transform",
                                        transform: "translateZ(0)",
                                        verticalAlign: "middle",
                                        touchAction: "pan-x pan-y pinch-zoom",
                                        overscrollBehaviorX: "contain",
                                    }}
                                >
                                    {typeLabels[row.type] ?? row.type}
                                </td>

                                {allYearQuarters.map((yq, i) => {
                                    const pos = cells[yq];
                                    const prevPos = i < allYearQuarters.length - 1 ? cells[allYearQuarters[i + 1]] : undefined;

                                    const columnTotal =
                                        totalsByYQ && totalsByYQ[yq] !== undefined
                                            ? totalsByYQ[yq]
                                            : allYearQuarters[0] === yq
                                                ? totalValueInNewestQuarter ?? 0
                                                : 0;

                                    const showNew = !!pos?.isNew && yqGte(yq, NEW_FLOOR_YQ);
                                    const showExited = !!pos?.isExited;
                                    const amendFlag = pos?.amendFlag;

                                    return (
                                        <td
                                            key={yq}
                                            style={{
                                                width: QUARTER_COL_W,
                                                minWidth: QUARTER_COL_W,
                                                backgroundColor: "#141414",
                                                color: "#FFF",
                                                verticalAlign: "top",
                                                textAlign: "left",
                                                border: 0,
                                                padding: 6,
                                                height: "100%",
                                                contain: "paint",
                                                boxSizing: "border-box",
                                            }}
                                        >
                                            <div style={{ height: "100%", ...gpuText }}>
                                                <PositionCell
                                                    pos={pos}
                                                    prevPos={prevPos}
                                                    showPositionShare={showPositionShare}
                                                    displayFields={displayFields}
                                                    columnTotal={columnTotal}
                                                    fill
                                                    isAggregatedRow={aggregatePositions}
                                                    isNew={showNew}
                                                    isExited={showExited}
                                                    amendFlag={amendFlag}
                                                />
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
